import { Subject } from "rxjs";

import { RequestCacheBuilder } from "./cache";
import type { AbortableRequest } from ".";

export type AssetDisplayChange = {
  workspaceId: number;
  schemaId: string;
  modelName: string;
};

export const assetDisplayChanges = new Subject<AssetDisplayChange>();
const revisions = new Map<string, number>();

/** Collection catalogs can embed several models; model-filtered requests stay scoped to that model. */
export function assetDisplayRequestScope(url: string): string | undefined {
  const parsed = new URL(url, "https://sitehub.invalid");
  const workspace = Number(
    parsed.searchParams.get("__sitehub_cache_workspace") ??
      parsed.searchParams.get("workspace__id") ??
      parsed.searchParams.get("workspaces__id")
  );
  if (!Number.isFinite(workspace) || workspace <= 0) return;
  let model: string | null | undefined;
  if (/^\/api\/fieldmodel\/?$/.test(parsed.pathname)) {
    model = parsed.searchParams.get("model");
  } else if (/^\/api\/custom_models\/schemas(?:\/|$)/.test(parsed.pathname)) {
    model = parsed.pathname.split("/")[4] || parsed.searchParams.get("id");
  } else if (/^\/api\/custom_models\/?$/.test(parsed.pathname)) {
    model = parsed.searchParams.get("schema__id");
  } else if (/^\/api\/custom_models\/[a-f0-9-]{36}\//i.test(parsed.pathname)) {
    model = parsed.pathname.split("/")[3];
  } else return;
  return `${workspace}:${model || "*"}`;
}

export function getAssetDisplayRevision(scope: string | undefined): number {
  return scope === undefined ? 0 : (revisions.get(scope) ?? 0);
}

/** Call only after the complete model Save succeeds, never for a modal draft. */
export function notifyAssetDisplaySaved(change: AssetDisplayChange): void {
  if (!change.workspaceId || !change.schemaId || !change.modelName) return;
  const scopes = new Set(
    ["*", change.schemaId, change.modelName].map(
      (model) => `${change.workspaceId}:${model}`
    )
  );
  scopes.forEach((scope) =>
    revisions.set(scope, getAssetDisplayRevision(scope) + 1)
  );
  RequestCacheBuilder.getInstance().deleteMatching((key) => {
    const scope = assetDisplayRequestScope(key);
    return scope !== undefined && scopes.has(scope);
  });
  assetDisplayChanges.next(change);
}

/** An older GET must neither paint nor repopulate the invalidated cache after Save. */
export function guardAssetDisplayResponse<T>(
  request: AbortableRequest<T>,
  scope: string | undefined,
  revision: number
): AbortableRequest<T> {
  if (scope === undefined) return request;
  return {
    abort: request.abort,
    response: request.response.then((response) => {
      if (revision !== getAssetDisplayRevision(scope))
        throw new DOMException(
          "Model display configuration changed",
          "AbortError"
        );
      return response;
    })
  };
}
